'use client'

import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useIframeCleanup } from './use-iframe-cleanup'

gsap.registerPlugin(useGSAP)

type PlayerIframePublicProps = {
  videoId: string
  onEnded?: () => void
  pause?: boolean
  autoplay?: boolean
  muted?: boolean
  loading?: 'eager' | 'lazy'
  className?: string
}

const BUNNY_LIBRARY_ID = '327014'

export function PlayerIframePublic({
  videoId,
  onEnded,
  pause,
  autoplay = true,
  muted = false,
  loading = 'lazy',
  className,
}: PlayerIframePublicProps) {
  const iframeContainerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const playerRef = useRef<any>(null)
  const active = useRef(true)
  const [PlayerConstructor, setPlayerConstructor] = useState<any>(null)

  const isActivePath = useCallback((pathname: string) => pathname.startsWith('/onboarding'), [])
  const beforeBlank = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.pause()
        playerRef.current.off('ready')
        playerRef.current.off('ended')
      } catch {}
    }
  }, [])

  useIframeCleanup(iframeRef, { isActivePath, beforeBlank })

  useGSAP(() => {
    const iframeContainer = iframeContainerRef.current
    const iframe = iframeContainer?.querySelector('iframe')
    if (iframe) {
      gsap.set(iframe, { opacity: 0, scale: 0.95 })
      gsap.to(iframe, { opacity: 1, scale: 1, duration: 1, ease: 'sine.out', delay: 1 })
    }
  })

  useEffect(() => {
    import('player.js').then((module) => {
      setPlayerConstructor(() => module.default.Player)
    })
  }, [])

  useEffect(() => {
    active.current = true
    const playerContainer = iframeContainerRef.current

    if (playerContainer && PlayerConstructor) {
      const iframe = playerContainer.querySelector('iframe')
      if (iframe?.src) {
        playerRef.current = new PlayerConstructor(iframe)

        playerRef.current.on('ready', () => {
          if (!active.current) return
          try {
            if (pause) playerRef.current.pause()
          } catch {}

          playerRef.current.on('ended', () => {
            if (!active.current) return
            if (onEnded) onEnded()
          })
        })
      }
    }

    return () => {
      active.current = false
      if (playerRef.current) {
        try {
          playerRef.current.pause()
          playerRef.current.off('ready')
          playerRef.current.off('ended')
        } catch {}
        playerRef.current.destroy()
        playerRef.current = null
      }
    }
  }, [videoId, pause, onEnded, PlayerConstructor])

  const src = `https://player.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${videoId}?autoplay=${autoplay ? 'true' : 'false'}&loop=false&muted=${muted ? 'true' : 'false'}&preload=true&responsive=true`

  return (
    <div className={cn('absolute inset-0 min-h-0', className)} ref={iframeContainerRef}>
      <iframe
        ref={iframeRef}
        title='Player'
        src={src}
        loading={loading}
        className='absolute top-0 left-0 h-full w-full border-0 bg-black'
        allow='accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;'
        allowFullScreen={true}
      ></iframe>
    </div>
  )
}
